import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom'; // <-- ADDED useLocation
import { MapPin, Briefcase, IndianRupee, X, Search, Filter, ArrowRight, UserCircle, Link } from 'lucide-react'; // <-- ADDED Link icon
import '../styles/CareersPage.css';

import JobPostingPreview from './Recruiter/JobPosting/JobPostingPreview'; 
import ExternalApplyModal from './ExternalApplyModal'; 

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
   
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    location: "",
    jobType: ""
  });

  const [selectedJob, setSelectedJob] = useState(null); 
  const [showApplyModal, setShowApplyModal] = useState(false); 
  const [locations, setLocations] = useState([]);

  const navigate = useNavigate(); 
  const location = useLocation(); // <-- ROUTING HOOK

  // --- CHECK IF USER IS LOGGED IN ---
  const isLoggedIn = !!(localStorage.getItem('userId') || localStorage.getItem('candidateId'));

  const isDeadlinePassed = (dateString) => {
    if (!dateString) return false; 
    const deadline = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadline.setHours(0, 0, 0, 0);
    return today > deadline;
  };

  const isJobVisible = (dateString) => {
    if (!dateString) return true; 
    const deadline = new Date(dateString);
    const today = new Date();
    const hideDate = new Date(deadline);
    hideDate.setDate(deadline.getDate() + 14); 
    return today <= hideDate;
  };

  useEffect(() => {
    const fetchPublicJobs = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings?type=public`);
        let jobData = response.data;

        jobData = jobData.filter(job => isJobVisible(job.applyByDate));

        setJobs(jobData);
        setFilteredJobs(jobData);
        
        const uniqueLocs = [...new Set(jobData.map(j => j.location).filter(Boolean))];
        setLocations(uniqueLocs);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching jobs", err);
        setLoading(false);
      }
    };
    fetchPublicJobs();
  }, []);

  // --- DEEP LINKING LOGIC: Auto-open modal if URL has ?jobId=... ---
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlJobId = queryParams.get('jobId');
    
    if (urlJobId && jobs.length > 0) {
      const foundJob = jobs.find(j => String(j.id || j._id) === String(urlJobId));
      if (foundJob) {
        setSelectedJob(foundJob);
      }
    }
  }, [location.search, jobs]);

  useEffect(() => {
    let result = jobs;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(j => 
        j.role.toLowerCase().includes(term) || j.companyName.toLowerCase().includes(term)
      );
    }
    if (filters.location) result = result.filter(j => j.location === filters.location);
    if (filters.jobType) result = result.filter(j => j.typeOfWork === filters.jobType);
    setFilteredJobs(result);
  }, [searchTerm, filters, jobs]);

  // --- THE LOGGED-IN APPLY LOGIC ---
  const handleApplyClick = async () => {
    if (!selectedJob || isDeadlinePassed(selectedJob.applyByDate)) return;

    if (!isLoggedIn) {
      navigate('/login'); 
      return;
    }

    if (selectedJob.isPremiumJob) {
      try {
        alert("This is a Premium Job. Redirecting to Stripe checkout...");
        setShowApplyModal(true);
      } catch (error) {
        console.error("Stripe initialization failed", error);
        alert("Failed to initialize payment gateway.");
      }
    } else {
      setShowApplyModal(true);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const openJobDetails = (job) => {
    setSelectedJob(job);
  };

  const closeJobDetails = () => {
    setSelectedJob(null);
  };

  // --- COPY LINK HANDLER ---
  const handleCopyLink = () => {
    if (!selectedJob) return;
    const currentJobId = selectedJob.id || selectedJob._id;
    const shareableLink = `${window.location.origin}/careers?jobId=${currentJobId}`;
    
    navigator.clipboard.writeText(shareableLink);
    alert('Job link copied to clipboard!'); 
  };

  // --- LOGIN NAVIGATION HANDLER ---
  const handleLoginRedirect = () => {
    if (selectedJob) {
      localStorage.setItem('pendingApplyJobId', selectedJob.id || selectedJob._id);
    }
    navigate('/login'); 
  };

  if (loading) return <div className="cp-loading">Loading Opportunities...</div>;

  return (
    <div id="careers-page-wrapper">
      <div className="cp-header-section">
        <div className="cp-content-max" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div>
                <h1>Elite Hospitality Careers</h1>
                <p>Connect with the world's most prestigious hotel brands.</p>
            </div>
            
            <div>
                {!isLoggedIn ? (
                    <button 
                        onClick={handleLoginRedirect} 
                        style={{ padding: '10px 20px', borderRadius: '8px', background: '#fff', color: '#0f172a', fontWeight: 'bold', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <UserCircle size={18} /> Login / Create Account
                    </button>
                ) : (
                    <div style={{ padding: '10px 20px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <UserCircle size={18} /> Logged In
                    </div>
                )}
            </div>
        </div>
      </div>

      <div className="cp-content-max cp-overlap-container">
        <div className="cp-filters-glass">
            <div className="cp-input-group">
                <Search className="cp-icon" size={18} />
                <input 
                  type="text" 
                  placeholder="Search by Job Role or Company..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            
            <div className="cp-divider"></div>

            <div className="cp-input-group">
                <MapPin className="cp-icon" size={18} />
                <select name="location" onChange={handleFilterChange}>
                  <option value="">All Locations</option>
                  {locations.map((loc, idx) => <option key={idx} value={loc}>{loc}</option>)}
                </select>
            </div>

            <div className="cp-divider"></div>

            <div className="cp-input-group">
                <Filter className="cp-icon" size={18} />
                <select name="jobType" onChange={handleFilterChange}>
                  <option value="">All Job Types</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Internship">Internship</option>
                  <option value="Part-time">Part-time</option>
                </select>
            </div>
        </div>

        <div className="cp-grid">
            {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => {
                const expired = isDeadlinePassed(job.applyByDate);
                return (
                <div key={job._id} className={`cp-card ${expired ? 'cp-expired' : ''}`} onClick={() => openJobDetails(job)}>
                    <div className="cp-card-top">
                        <div className="cp-role-info">
                            <h3>{job.role}</h3>
                            <span className="cp-company">{job.companyName}</span>
                        </div>
                        {expired ? <span className="cp-status-badge closed">Closed</span> : <span className="cp-status-badge active">Hiring</span>}
                    </div>

                    <div className="cp-card-details">
                        <span><MapPin size={14}/> {job.location || 'N/A'}</span>
                        <span><Briefcase size={14}/> {job.typeOfWork || 'Full-time'}</span>
                        <span><IndianRupee size={14}/> {job.ctc || 'Disclosed on selection'}</span>
                    </div>

                    <div className="cp-card-action">
                        {expired ? (
                            <button className="cp-link-btn disabled" disabled>
                                Applications Closed
                            </button>
                        ) : (
                            <button className="cp-link-btn">
                                View Details <ArrowRight size={16}/>
                            </button>
                        )}
                    </div>
                </div>
                );
            })
            ) : (
            <div className="cp-no-jobs">
                <h3>No opportunities found.</h3>
                <p>Try adjusting your search criteria.</p>
            </div>
            )}
        </div>
      </div>

      {selectedJob && !showApplyModal && (
        <div className="cp-modal-backdrop" onClick={closeJobDetails}>
          <div className="cp-modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="cp-modal-head">
              <div>
                <h2>{selectedJob.role}</h2>
                <p className="cp-modal-subtitle">{selectedJob.companyName}</p>
                {isDeadlinePassed(selectedJob.applyByDate) && (
                     <span className="cp-status-badge closed" style={{marginTop:'8px', display:'inline-block'}}>EXPIRED</span>
                )}
              </div>
              <button className="cp-close-circle" onClick={closeJobDetails}>
                <X size={20} />
              </button>
            </div>

            <div className="cp-modal-scroll-area">
              <JobPostingPreview formData={selectedJob} />
            </div>

            <div className="cp-modal-foot" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* --- COPY LINK BUTTON --- */}
              <button 
                onClick={handleCopyLink}
                style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#475569', fontWeight: 'bold' }}
              >
                <Link size={16} /> Copy Link
              </button>

              {/* --- CONDITIONAL MODAL BUTTON --- */}
              <div>
                {isDeadlinePassed(selectedJob.applyByDate) ? (
                   <button className="cp-primary-btn disabled" disabled>
                     Applications Closed
                   </button>
                ) : !isLoggedIn ? (
                   <button className="cp-primary-btn" onClick={handleLoginRedirect}>
                     Login to Apply
                   </button>
                ) : (
                   <button className="cp-primary-btn" onClick={handleApplyClick}>
                      {selectedJob.isPremiumJob ? "Pay & Apply for Position" : "Apply for Position"}
                   </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showApplyModal && selectedJob && (
        <ExternalApplyModal 
            job={selectedJob} 
            onClose={() => { setShowApplyModal(false); closeJobDetails(); }} 
        />
      )}
    </div>
  );
};

export default CareersPage;